// ABOUTME: Context for managing admin justification prompts and headers
// ABOUTME: Provides modal UI and automatic header injection for cross-family access

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  Alert
} from '@mui/material';

interface JustificationRequest {
  resourceType: string;
  resourceId: number | string;
  resourceName?: string;
  resolve: (justification: string | null) => void;
}

interface AdminJustificationContextType {
  requestJustification: (resourceType: string, resourceId: number | string, resourceName?: string) => Promise<string | null>;
  currentJustification: string | null;
}

const AdminJustificationContext = createContext<AdminJustificationContextType | null>(null);

export const useAdminJustification = () => {
  const context = useContext(AdminJustificationContext);
  if (!context) {
    throw new Error('useAdminJustification must be used within AdminJustificationProvider');
  }
  return context;
};

const PREDEFINED_JUSTIFICATIONS = [
  'Password Reset',
  'Data Recovery',
  'User Support Request',
  'Technical Investigation',
  'Account Issue Resolution',
  'Data Export Request',
  'Other'
];

export const AdminJustificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedJustification, setSelectedJustification] = useState('');
  const [otherJustification, setOtherJustification] = useState('');
  const [currentRequest, setCurrentRequest] = useState<JustificationRequest | null>(null);
  const [currentJustification, setCurrentJustification] = useState<string | null>(null);

  const requestJustification = useCallback((resourceType: string, resourceId: number | string, resourceName?: string): Promise<string | null> => {
    return new Promise((resolve) => {
      setCurrentRequest({ resourceType, resourceId, resourceName, resolve });
      setIsModalOpen(true);
      setSelectedJustification('');
      setOtherJustification('');
    });
  }, []);

  const handleSubmit = () => {
    if (!currentRequest) return;

    let justification = selectedJustification;
    if (selectedJustification === 'Other') {
      if (!otherJustification.trim()) {
        // Don't close modal if "Other" is selected but no text provided
        return;
      }
      justification = `Other: ${otherJustification.trim()}`;
    }

    setCurrentJustification(justification);
    currentRequest.resolve(justification);
    setIsModalOpen(false);
    setCurrentRequest(null);
  };

  const handleCancel = () => {
    if (currentRequest) {
      currentRequest.resolve(null);
    }
    setIsModalOpen(false);
    setCurrentRequest(null);
  };

  return (
    <AdminJustificationContext.Provider value={{ requestJustification, currentJustification }}>
      {children}
      
      <Dialog 
        open={isModalOpen} 
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Admin Access Justification Required</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            You are attempting to access data outside of your assigned families. 
            Please provide a justification for this cross-family access.
          </Alert>
          
          {currentRequest && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Accessing: {currentRequest.resourceName ? 
                `${currentRequest.resourceName} (${currentRequest.resourceType} #${currentRequest.resourceId})` : 
                `${currentRequest.resourceType} #${currentRequest.resourceId}`
              }
            </Typography>
          )}

          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={selectedJustification}
              onChange={(e) => setSelectedJustification(e.target.value)}
            >
              {PREDEFINED_JUSTIFICATIONS.map((justification) => (
                <FormControlLabel
                  key={justification}
                  value={justification}
                  control={<Radio />}
                  label={justification}
                />
              ))}
            </RadioGroup>
          </FormControl>

          {selectedJustification === 'Other' && (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Please describe your reason"
                value={otherJustification}
                onChange={(e) => setOtherJustification(e.target.value)}
                placeholder="Enter a detailed justification..."
                error={selectedJustification === 'Other' && !otherJustification.trim()}
                helperText={selectedJustification === 'Other' && !otherJustification.trim() ? 'Description required when selecting "Other"' : ''}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!selectedJustification || (selectedJustification === 'Other' && !otherJustification.trim())}
          >
            Submit Justification
          </Button>
        </DialogActions>
      </Dialog>
    </AdminJustificationContext.Provider>
  );
};